import { Undo2 } from 'lucide-react';

/**
 * Bottom-centre undo snackbar.
 *
 * Rendered only while a snapshot is fresh. Deliberately not a permanent
 * "Undo" control: the Clear and per-entry remove buttons keep their existing
 * appearance, so nothing about the page shifts when this appears — it sits
 * over the content rather than in the flow.
 *
 * Uses the same Tailwind dark: convention as the rest of the site rather than
 * raw CSS variables, because that is what every other component here does and
 * a second theming mechanism would be the inconsistency, not the fix.
 */
export default function UndoToast({
  label,
  onUndo,
}: {
  label: string;
  onUndo: () => void;
}) {
  return (
    <div
      // `status`, not `alert`: this is a passive confirmation, and `alert`
      // interrupts a screen reader mid-sentence for something that is not urgent.
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-lg
                 bg-gray-900 text-gray-100 border border-gray-700
                 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-300"
    >
      <span className="text-sm font-medium">{label}</span>
      <span aria-hidden="true" className="text-gray-500 dark:text-gray-400">—</span>
      <button
        type="button"
        onClick={onUndo}
        className="inline-flex items-center gap-1.5 text-sm font-bold rounded-lg px-2 py-1
                   text-violet-300 hover:text-violet-200 hover:bg-white/10
                   dark:text-violet-700 dark:hover:text-violet-800 dark:hover:bg-black/5
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 transition-colors"
      >
        <Undo2 size={14} aria-hidden="true" />
        Undo
      </button>
      {/* The shortcut is worth stating: it is the only way to reach snapshots
          older than the one this toast is offering. */}
      <kbd className="hidden sm:inline text-[10px] font-semibold px-1.5 py-0.5 rounded
                      bg-white/10 text-gray-400 dark:bg-black/10 dark:text-gray-500">
        Ctrl+Z
      </kbd>
    </div>
  );
}
